/**
 * Shared parts of the deep-link steps (Sync products, Blank products, Blank
 * stock; handover 4.4): the work happens on another page, so the pane shows
 * the stat, a note naming that page, and ONE footer primary "Go to <page>".
 */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { PaneActionButton } from '../shell/PaneActionButton';
import { useSetupMode } from '../../hooks/useSetupMode';

/** The pages a deep-link step opens, with their already literal label keys. */
const DEEP_LINK_PAGES = {
  productSync: { to: '/product-sync', labelKey: 'deepLink.page.productSync' },
  products: { to: '/products', labelKey: 'deepLink.page.products' },
  // Never '/': that lands a client on /orders (HomeRouteSwitch).
  inventory: { to: '/inventory', labelKey: 'deepLink.page.inventory' },
} as const;

export type DeepLinkPage = keyof typeof DEEP_LINK_PAGES;

export function DeepLinkGo({ page }: { page: DeepLinkPage }) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const target = DEEP_LINK_PAGES[page];
  return (
    <PaneActionButton type="button" onClick={() => navigate(target.to)}>
      {t('deepLink.go', { page: t(target.labelKey) })}
      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
    </PaneActionButton>
  );
}

/** The big number and its label. */
export function DeepLinkStat({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

/** In setup mode the note points at the Back to setup bar that page will show. */
export function DeepLinkNote({ page }: { page: DeepLinkPage }) {
  const { t } = useTranslation('onboarding');
  const inSetupMode = useSetupMode();
  return (
    <p className="mt-4 max-w-prose text-xs text-muted-foreground">
      {t(inSetupMode ? 'deepLink.noteSetupMode' : 'deepLink.note', { page: t(DEEP_LINK_PAGES[page].labelKey) })}
    </p>
  );
}
